import { Button, Checkbox, Divider, Group, NumberInput, SegmentedControl, Stack, Text, TextInput } from "@mantine/core";
import { IMulticonsultProject } from "@mctypes/global.types";
import { IMaconomyCoordinates } from "@mctypes/report.types";
import * as R from "ramda";
import React, { useEffect } from "react";
import { setShowSuccessMessageThunk } from "../../../../global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { ForenkletEllerDetaljertEnum } from "@mcapps/shared/reportEnums";
import { IBygningsfysikkPremissRapport } from "@mcapps/shared/reportSchemas/bygningsfysikk/bygningsfysikkPremissrapport/bygningsfysikkPremissrapportSchema";
import { convertMaconomyCoordinatesToGeoteknikCoordinates } from "../geoteknik/GeoteknikMaps";
import { doGetSingleMaconomyProject } from "../reportBoardAPI";

const BygningsfysikkPremissrapport: React.FC<{
  createdAt?: string;
  data: IBygningsfysikkPremissRapport;
  onUpdateEditData: (data: IBygningsfysikkPremissRapport) => void;
}> = ({ data, onUpdateEditData }) => {
  const [editData, setEditData] = React.useState<IBygningsfysikkPremissRapport>(R.clone(data));
  const [failedToFetchFromMaconomy, setFailedToFetchFromMaconomy] = React.useState<boolean>(false);
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.adminReducer.project);

  useEffect(() => {
    onUpdateEditData(data);
  }, []);

  const handleChange = (path: string, value: boolean) => {
    setEditData((prev) => {
      const newData = structuredClone(prev);
      const keys = path.split(".");
      let current: any = newData;

      for (let i = 0; i < keys.length - 1; i++) current = current[keys[i]];

      current[keys[keys.length - 1]].isActive = value;

      if (!value) {
        const setChildrenInactive = (obj: any) => {
          Object.values(obj).forEach((child: any) => {
            if (child && typeof child === "object" && "isActive" in child) {
              child.isActive = false;
              setChildrenInactive(child);
            }
          });
        };
        setChildrenInactive(current[keys[keys.length - 1]]);
      }

      onUpdateEditData(newData);
      return newData;
    });
  };

  const handleGenerellInfoChange = (path: string, value: any) => {
    setEditData((prev) => {
      const newData = structuredClone(prev);
      const keys = path.split(".");
      let current: any = newData.generellInfo;
      for (let i = 0; i < keys.length - 1; i++) current = current[keys[i]];
      current[keys[keys.length - 1]] = value;
      onUpdateEditData(newData);
      return newData;
    });
  };

  const setValueByPath = (obj: any, path: string, value: any) => {
    const keys = path.split(".");
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) current = current[keys[i]];
    current[keys[keys.length - 1]] = value;
  };

  const handleToggleValueChange = (path: string, value: any) => {
    setEditData((prev) => {
      const newData = structuredClone(prev);
      setValueByPath(newData, path, value);
      onUpdateEditData(newData);
      return newData;
    });
  };

  const fetchFromMaconomyAndSetData = async () => {
    try {
      setFailedToFetchFromMaconomy(false);
      const multiconsultProject: IMulticonsultProject = await doGetSingleMaconomyProject(project._id);
      if (multiconsultProject == null) {
        return;
      }
      dispatch(setShowSuccessMessageThunk("Hentet data fra maconomy"));

      const maconomyCoordinates: IMaconomyCoordinates = {
        UTMN:
          typeof multiconsultProject.UTMN === "string"
            ? Number(multiconsultProject.UTMN.replace(",", "."))
            : multiconsultProject.UTMN,
        GNR: Number(multiconsultProject.GNR),
        BNR: Number(multiconsultProject.BNR),
        UTME:
          typeof multiconsultProject.UTME === "string"
            ? Number(multiconsultProject.UTME.replace(",", "."))
            : multiconsultProject.UTME,
        UTMZ: Number(multiconsultProject.UTMZ),
      };

      const koordinater = convertMaconomyCoordinatesToGeoteknikCoordinates(maconomyCoordinates);

      let updatedData = R.clone(editData);
      updatedData.generellInfo.oppdragsnavn = multiconsultProject.DisplayName;
      updatedData.generellInfo.oppdragsgiver = multiconsultProject.CustomerName;
      updatedData.generellInfo.kontaktperson = multiconsultProject.InvoiceName;
      updatedData.generellInfo.koordinater = koordinater;
      updatedData.generellInfo.oppdragsleder = (multiconsultProject.ProjectManager as any)?.FullName;
      updatedData.generellInfo.eiendomsInformasjon.bnr = Number(multiconsultProject.BNR);
      updatedData.generellInfo.eiendomsInformasjon.gnr = Number(multiconsultProject.GNR);

      const ansvarligEnhet = BYGNINGSFYSIKK_DEPARTMENTS.find((d) => d.startsWith(String(multiconsultProject.UnitId)));

      updatedData.generellInfo.ansvarligEnhet = ansvarligEnhet || "";

      setEditData({
        ...editData,
        ...updatedData,
      });
    } catch (error) {
      setFailedToFetchFromMaconomy(true);
    }
  };

  const renderGenerellInfoInputs = () => {
    const info = editData.generellInfo;
    return (
      <Stack>
        <Divider
          my="sm"
          label={
            <Text fz="md" fw={600}>
              Generell informasjon
            </Text>
          }
          mb="xs"
        />
        <Button onClick={() => fetchFromMaconomyAndSetData()}>Last ned data fra maconomy</Button>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Oppdragsnavn:
          </Text>
          <TextInput
            size="sm"
            value={info.oppdragsnavn}
            onChange={(e) => handleGenerellInfoChange("oppdragsnavn", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Emne:
          </Text>
          <TextInput
            size="sm"
            value={info.emne}
            onChange={(e) => handleGenerellInfoChange("emne", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Oppdragsgiver:
          </Text>
          <TextInput
            size="sm"
            value={info.oppdragsgiver}
            onChange={(e) => handleGenerellInfoChange("oppdragsgiver", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Kontaktperson:
          </Text>
          <TextInput
            size="sm"
            value={info.kontaktperson}
            onChange={(e) => handleGenerellInfoChange("kontaktperson", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Dokumentkode:
          </Text>
          <TextInput
            size="sm"
            value={info.dokumentkode}
            onChange={(e) => handleGenerellInfoChange("dokumentkode", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Tilgjengelighet:
          </Text>
          <TextInput
            size="sm"
            value={info.tilgjengelighet}
            onChange={(e) => handleGenerellInfoChange("tilgjengelighet", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Oppdragsleder:
          </Text>
          <TextInput
            size="sm"
            value={info.oppdragsleder}
            onChange={(e) => handleGenerellInfoChange("oppdragsleder", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Utarbeidet av:
          </Text>
          <TextInput
            size="sm"
            value={info.utarbeidetAv}
            onChange={(e) => handleGenerellInfoChange("utarbeidetAv", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Ansvarlig enhet:
          </Text>
          <TextInput
            size="sm"
            value={info.ansvarligEnhet}
            onChange={(e) => handleGenerellInfoChange("ansvarligEnhet", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Divider
          my="sm"
          label={
            <Text fz="md" fw={600}>
              Koordinater
            </Text>
          }
          mb="xs"
        />
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Øst:
          </Text>
          <NumberInput
            size="sm"
            value={info.koordinater.ost}
            onChange={(value) => handleGenerellInfoChange("koordinater.ost", value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Nord:
          </Text>
          <NumberInput
            size="sm"
            value={info.koordinater.nord}
            onChange={(value) => handleGenerellInfoChange("koordinater.nord", value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Sone:
          </Text>
          <NumberInput
            size="sm"
            value={info.koordinater.sone}
            onChange={(value) => handleGenerellInfoChange("koordinater.sone", value)}
            flex={1}
          />
        </Group>
        <Divider
          my="sm"
          label={
            <Text fz="md" fw={600}>
              Eiendomsinformasjon
            </Text>
          }
          mb="xs"
        />
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Gnr:
          </Text>
          <NumberInput
            size="sm"
            value={info.eiendomsInformasjon.gnr}
            onChange={(value) => handleGenerellInfoChange("eiendomsInformasjon.gnr", value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Bnr:
          </Text>
          <NumberInput
            size="sm"
            value={info.eiendomsInformasjon.bnr}
            onChange={(value) => handleGenerellInfoChange("eiendomsInformasjon.bnr", value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Snr:
          </Text>
          <NumberInput
            size="sm"
            value={info.eiendomsInformasjon.snr}
            onChange={(value) => handleGenerellInfoChange("eiendomsInformasjon.snr", value)}
            flex={1}
          />
        </Group>
        <Group gap="sm" align="center">
          <Text w={120} size="sm">
            Kommune:
          </Text>
          <TextInput
            size="sm"
            value={info.eiendomsInformasjon.kommune || ""}
            onChange={(e) => handleGenerellInfoChange("eiendomsInformasjon.kommune", e.currentTarget.value)}
            flex={1}
          />
        </Group>
        <Divider
          my="sm"
          label={
            <Text fz="md" fw={600}>
              Kapitler
            </Text>
          }
          mb="xs"
        />
      </Stack>
    );
  };

  const renderCheckboxes = (obj: any, path: string = "", depth: number = 0) => {
    return Object.entries(obj).map(([key, value]: any) => {
      if (!value || typeof value !== "object") return null;

      const currentPath = path ? `${path}.${key}` : key;

      const hasCheckbox = "isActive" in value;
      const hasForenkletDetaljert = "forenkletEllerDetaljert" in value;
      const forenkletPath = `${currentPath}.forenkletEllerDetaljert`;

      const children = Object.entries(value)
        .filter(([k, v]: any) => v && typeof v === "object")
        .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

      // indent per level (feels much clearer in nested structures)
      const baseIndent = 12;
      const indent = depth * baseIndent;

      return (
        <Stack key={currentPath} gap={8} style={{ marginLeft: indent }}>
          {hasCheckbox && (
            <Checkbox
              checked={value.isActive}
              onChange={(e) => handleChange(currentPath, e.currentTarget.checked)}
              label={
                <Text fw={600} mb={0}>
                  {value.tekst}
                </Text>
              }
            />
          )}

          {hasForenkletDetaljert && (hasCheckbox ? value.isActive : true) && (
            <Group ml={32} align="center" gap="xs" mb="xs">
              <Text size="sm" fw={600}>
                Detaljeringsnivå:
              </Text>

              <SegmentedControl
                size="xs"
                color="mc-primary"
                value={value.forenkletEllerDetaljert ?? ""}
                onChange={(v) =>
                  handleToggleValueChange(forenkletPath, v === "" ? null : (v as ForenkletEllerDetaljertEnum))
                }
                data={[
                  { label: "Forenklet", value: ForenkletEllerDetaljertEnum.FORENKLET },
                  { label: "Detaljert", value: ForenkletEllerDetaljertEnum.DETALJERT },
                ]}
                styles={{
                  control: {
                    minWidth: 70,
                  },
                  label: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  },
                }}
              />
            </Group>
          )}

          {Object.keys(children).length > 0 &&
            (hasCheckbox ? value.isActive : true) &&
            renderCheckboxes(children, currentPath, depth + 1)}
        </Stack>
      );
    });
  };

  // Wrap each main chapter in a Card
  const renderChapters = () => {
    const chapterKeys = Object.keys(editData.kapitler);
    return chapterKeys.map((chapterKey, index) => (
      <React.Fragment key={chapterKey}>
        {renderCheckboxes({ [chapterKey]: editData.kapitler[chapterKey] }, "kapitler")}
        {index < chapterKeys.length - 1 && <Divider />}
      </React.Fragment>
    ));
  };

  return (
    <Stack p="md" style={{ maxWidth: 600, width: "100%", margin: "0 auto" }}>
      <Text fw={700} size="xl">
        Bygningsfysikk Premissrapport
      </Text>
      {renderGenerellInfoInputs()}
      {renderChapters()}
    </Stack>
  );
};

export default BygningsfysikkPremissrapport;

export const BYGNINGSFYSIKK_DEPARTMENTS = [
  "-",
  "10234021 Seksjon Spesialrådgivning TRL",
  "10233026 Bygningsforvaltning, Bygningsfysikk og Miljørådgivning",
  "10232043 Seksjon Bygningsforvaltning og bygningsfysikk",
  "10106050 Seksjon Bygningsfysikk",
];
